import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  BrickWall,
  Building2,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  LayoutTemplate,
  Link2,
  Plus,
  Trash2,
  Unlink,
} from "lucide-react";
import client, { absoluteUrl } from "../api/client";
import Modal from "../Moodboard/Modal";
import { buildTemplate } from "../Moodboard/templates";
import { LengthInput, ProductPicker, UnitToggle, downloadFile, productName } from "./common";
import FirmProfileDialog from "./FirmProfileDialog";
import { STATUSES } from "./ProjectList";
import { SOLD_AS } from "./quantities";
import { formatLength, formatMoney, formatSize } from "./units";
import "../Moodboard/Moodboard.css";
import "./Architect.css";

// Text that saves when the field is left, not on every key.
function BlurInput({ value, onCommit, multiline = false, ...rest }) {
  const [text, setText] = useState(value ?? "");
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(value ?? "");
  }, [value]);
  const Tag = multiline ? "textarea" : "input";
  return (
    <Tag
      {...rest}
      value={text}
      onFocus={() => {
        focused.current = true;
      }}
      onChange={(event) => setText(event.target.value)}
      onBlur={() => {
        focused.current = false;
        if (rest.required && !text.trim()) {
          setText(value ?? "");
          return;
        }
        if ((text ?? "") !== (value ?? "")) onCommit(text);
      }}
      onKeyDown={(event) => !multiline && event.key === "Enter" && event.currentTarget.blur()}
    />
  );
}

const plural = (quantity, unit) => (unit === "sq ft" || quantity === 1 ? unit : `${unit}s`);

function workingText(line, units) {
  const { working } = line;
  const detail = working.detail || {};
  if (line.qty_mode === "manual") return "Entered by hand";
  if (working.missing?.length) {
    const needs = { wall: "wall size", roll: "roll size", panel: "panel size" };
    return `Needs ${working.missing.map((key) => needs[key]).join(" and ")}`;
  }
  if (line.spec.sold_as === "area") return `${detail.area_sq_ft} sq ft + ${line.wastage_pct}%`;
  if (line.spec.sold_as === "roll") return `${detail.strips} strips, ${detail.strips_per_roll || "<1"} per roll`;
  if (line.spec.sold_as === "panel") return `${detail.across} × ${detail.down} panels`;
  if (detail.suggested_width_mm) {
    return `Suits ${formatLength(detail.suggested_width_mm[0], units)}–${formatLength(detail.suggested_width_mm[1], units)} wide`;
  }
  return null;
}

function LineDetails({ line, units, onSave }) {
  const catalogue = line.catalog || {};
  const overrides = line.overrides || {};
  const setOverride = (key, value) => {
    const next = { ...overrides };
    if (value === null || value === "" || value === undefined || value === catalogue[key]) delete next[key];
    else next[key] = value;
    onSave({ overrides: next });
  };
  const soldAs = line.spec.sold_as;
  const sizeLabels = { roll: ["Roll width", "Roll length"], panel: ["Panel width", "Panel height"], piece: ["Width", "Height"] }[soldAs];

  return (
    <div className="ar-line-details">
      <div className="ar-field">
        <span className="ar-field-label">Sold as</span>
        <div className="ar-segmented ar-segmented-wrap" role="radiogroup" aria-label="Sold as">
          {SOLD_AS.map((entry) => (
            <button
              key={entry.key}
              type="button"
              role="radio"
              aria-checked={soldAs === entry.key}
              className={soldAs === entry.key ? "active" : ""}
              onClick={() => setOverride("sold_as", entry.key)}
            >
              {entry.label}
            </button>
          ))}
        </div>
      </div>
      {sizeLabels && (
        <div className="ar-row">
          <LengthInput label={sizeLabels[0]} value={line.spec.width_mm} units={units} onChange={(mm) => setOverride("width_mm", mm)} />
          <LengthInput label={sizeLabels[1]} value={line.spec.height_mm} units={units} onChange={(mm) => setOverride("height_mm", mm)} />
          {soldAs === "roll" && (
            <LengthInput label="Pattern repeat" value={line.spec.repeat_mm} units={units} onChange={(mm) => setOverride("repeat_mm", mm)} placeholder="none" />
          )}
        </div>
      )}
      <div className="ar-row">
        <label className="ar-field">
          <span className="ar-field-label">Material</span>
          <BlurInput type="text" maxLength={120} value={line.spec.material || ""} placeholder={catalogue.material || ""} onCommit={(text) => setOverride("material", text.trim() || null)} />
        </label>
        <label className="ar-field">
          <span className="ar-field-label">Finish</span>
          <BlurInput type="text" maxLength={120} value={line.spec.finish || ""} placeholder={catalogue.finish || ""} onCommit={(text) => setOverride("finish", text.trim() || null)} />
        </label>
        <label className="ar-field">
          <span className="ar-field-label">Code</span>
          <BlurInput type="text" maxLength={40} value={line.spec.code || ""} placeholder={catalogue.code || ""} onCommit={(text) => setOverride("code", text.trim() || null)} />
        </label>
      </div>
      <div className="ar-row">
        <label className="ar-field">
          <span className="ar-field-label">Your cost {SOLD_AS.find((entry) => entry.key === soldAs).per} (INR)</span>
          <BlurInput
            type="number"
            min={0}
            step="0.01"
            value={overrides.price ?? ""}
            placeholder={catalogue.price != null ? `${catalogue.price} (catalogue)` : "Price on request"}
            onCommit={(text) => setOverride("price", text === "" ? null : text)}
          />
        </label>
        {soldAs !== "piece" && (
          <label className="ar-field">
            <span className="ar-field-label">Extra for wastage (%)</span>
            <BlurInput
              type="number"
              min={0}
              max={100}
              step="0.5"
              value={String(line.wastage_pct)}
              onCommit={(text) => onSave({ wastage_pct: text === "" ? null : text })}
            />
          </label>
        )}
      </div>
      <label className="ar-field">
        <span className="ar-field-label">Note on the quotation</span>
        <BlurInput type="text" maxLength={500} value={line.note || ""} onCommit={(text) => onSave({ note: text })} placeholder="e.g. Install after painting" />
      </label>
      {Object.keys(overrides).length > 0 && (
        <button type="button" className="mb-text-button" onClick={() => onSave({ overrides: {} })}>
          Use catalogue details again
        </button>
      )}
    </div>
  );
}

function ProjectLine({ line, units, index, total, onSave, onRemove, onMove }) {
  const [open, setOpen] = useState(false);
  const spec = line.spec;
  const size =
    spec.sold_as === "area" ? null : formatSize(spec.width_mm, spec.height_mm, units) && `${spec.sold_as === "roll" ? "Roll " : ""}${formatSize(spec.width_mm, spec.height_mm, units)}`;
  const summary = [spec.code && `Code ${spec.code}`, size, [spec.material, spec.finish].filter(Boolean).join(", ")].filter(Boolean).join(" · ");
  const working = workingText(line, units);

  return (
    <article className={`ar-line${line.available ? "" : " is-unavailable"}`}>
      <div className="ar-line-main">
        {line.thumbnail_path || line.preview_url ? (
          <img className="ar-line-thumb" src={absoluteUrl(line.thumbnail_path || line.preview_url) || undefined} alt="" loading="lazy" />
        ) : (
          <span className="ar-line-thumb" aria-hidden="true" />
        )}
        <div className="ar-line-product">
          <strong>{productName(line)}</strong>
          <span>{summary || SOLD_AS.find((entry) => entry.key === spec.sold_as).label}</span>
          {!line.available && <span className="ar-warning">No longer in the catalogue</span>}
          <BlurInput
            className="ar-line-room"
            type="text"
            maxLength={120}
            value={line.room || ""}
            placeholder="Room or wall"
            aria-label="Room or wall"
            onCommit={(text) => onSave({ room: text })}
          />
        </div>
        <div className="ar-line-wall">
          <LengthInput label="Wall width" value={line.wall_w_mm} units={units} onChange={(mm) => onSave({ wall_w_mm: mm })} />
          <LengthInput label="Wall height" value={line.wall_h_mm} units={units} onChange={(mm) => onSave({ wall_h_mm: mm })} />
        </div>
        <div className="ar-line-qty">
          {line.qty_mode === "manual" ? (
            <label className="ar-field">
              <span className="ar-field-label">Quantity ({plural(2, spec.unit)})</span>
              <BlurInput type="number" min={0} step="any" value={String(line.quantity ?? "")} onCommit={(text) => onSave({ quantity: text })} />
            </label>
          ) : (
            <p className="ar-qty-figure">{line.quantity == null ? "—" : `${line.quantity} ${plural(line.quantity, spec.unit)}`}</p>
          )}
          {working && <p className={`ar-small ${line.working.missing?.length ? "ar-warning" : "ar-muted"}`}>{working}</p>}
          <button
            type="button"
            className="mb-text-button ar-small"
            onClick={() =>
              // Typing starts from the worked-out figure, not from scratch.
              onSave(line.qty_mode === "manual" ? { qty_mode: "auto" } : { qty_mode: "manual", quantity: line.quantity ?? 1 })
            }
          >
            {line.qty_mode === "manual" ? "Work it out from the wall" : "Enter by hand"}
          </button>
        </div>
        <div className="ar-line-money">
          <span className="ar-muted ar-small">{line.rate == null ? "Price on request" : `INR ${formatMoney(line.rate)} ${SOLD_AS.find((e) => e.key === spec.sold_as).per}`}</span>
          <strong>{line.amount == null ? "—" : `INR ${formatMoney(line.amount)}`}</strong>
        </div>
        <div className="ar-line-actions">
          <button type="button" className="mb-icon-button" onClick={() => onMove(-1)} disabled={index === 0} aria-label="Move up" title="Move up">
            <ArrowUp size={14} strokeWidth={1.8} />
          </button>
          <button type="button" className="mb-icon-button" onClick={() => onMove(1)} disabled={index === total - 1} aria-label="Move down" title="Move down">
            <ArrowDown size={14} strokeWidth={1.8} />
          </button>
          <button type="button" className="mb-icon-button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-label="Edit details" title="Edit details">
            {open ? <ChevronUp size={15} strokeWidth={1.8} /> : <ChevronDown size={15} strokeWidth={1.8} />}
          </button>
          <button type="button" className="mb-icon-button danger" onClick={onRemove} aria-label="Remove" title="Remove from project">
            <Trash2 size={14} strokeWidth={1.8} />
          </button>
        </div>
      </div>
      {open && <LineDetails line={line} units={units} onSave={onSave} />}
    </article>
  );
}

function LinkPicker({ kind, project, onClose, onLink }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const url = kind === "board" ? "/api/moodboards" : "/api/visualisations";
    client
      .get(url)
      .then(({ data }) => setRows((data.items || []).filter((row) => row.project_id !== project.id)))
      .catch(() => setRows([]));
  }, [kind, project.id]);
  return (
    <Modal title={kind === "board" ? "Add an existing mood board" : "Add an existing wall"} onClose={onClose}>
      {rows == null ? (
        <p className="ar-muted">Loading...</p>
      ) : !rows.length ? (
        <p className="ar-muted">Nothing else to add.</p>
      ) : (
        <ul className="ar-project-choices">
          {rows.map((row) => (
            <li key={row.id}>
              <button type="button" onClick={() => onLink(row.id)}>
                <strong>{row.title}</strong>
                <span>{row.project_id ? "In another project" : "Not in a project"}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function DocumentDialog({ project, onClose }) {
  const [kind, setKind] = useState("quote");
  const [visuals, setVisuals] = useState(project.visualisations.length > 0);
  const [busy, setBusy] = useState(false);
  const make = async () => {
    setBusy(true);
    const ok = await downloadFile(`/api/projects/${project.id}/document`, { kind, visuals: visuals ? 1 : 0 }, `${project.name}.pdf`);
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Modal title="Quotation or spec sheet" onClose={onClose}>
      <div className="ar-form">
        <div className="ar-choice-cards">
          {[
            ["quote", "Quotation", `Rates with your ${project.markup_pct}% markup, GST ${project.gst_pct}% and the total.`],
            ["spec", "Spec sheet", "Products, sizes, materials and quantities, without prices."],
          ].map(([key, title, text]) => (
            <button key={key} type="button" className={kind === key ? "active" : ""} onClick={() => setKind(key)} aria-pressed={kind === key}>
              <strong>{title}</strong>
              <span>{text}</span>
            </button>
          ))}
        </div>
        {project.visualisations.length > 0 && (
          <label className="mb-toggle">
            <input type="checkbox" checked={visuals} onChange={(event) => setVisuals(event.target.checked)} />
            <span>Add the wall visualisations as pages</span>
          </label>
        )}
        {project.totals.incomplete > 0 && kind === "quote" && (
          <p className="ar-warning ar-small">
            {project.totals.incomplete} product{project.totals.incomplete === 1 ? " has" : "s have"} no price or quantity yet and
            will show as on request.
          </p>
        )}
        <div className="mb-modal-actions">
          <button type="button" className="mb-text-button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="mb-primary" onClick={make} disabled={busy}>
            <FileText size={15} strokeWidth={1.9} aria-hidden="true" />
            {busy ? "Preparing PDF..." : "Download PDF"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    client
      .get(`/api/projects/${projectId}`)
      .then(({ data }) => setProject(data))
      .catch((err) => setError(err.response?.status === 404 ? "This project was not found." : "Could not load the project."));
  }, [projectId]);

  // Every change returns the whole project, worked out again on the server.
  const call = useCallback(async (request, failure) => {
    try {
      const { data } = await request;
      setProject(data);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.error || failure);
      return null;
    }
  }, []);

  const update = (patch) => call(client.put(`/api/projects/${projectId}`, patch), "Could not save the project.");
  const saveLine = (line) => (patch) =>
    call(client.put(`/api/projects/${projectId}/items/${line.id}`, patch), "Could not save that product.");
  const removeLine = (line) => {
    if (!window.confirm(`Remove ${productName(line)} from this project?`)) return;
    call(client.delete(`/api/projects/${projectId}/items/${line.id}`), "Could not remove that product.");
  };
  const moveLine = (index, delta) => {
    const ids = project.lines.map((line) => line.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(index + delta, 0, moved);
    call(client.post(`/api/projects/${projectId}/items/order`, { ids }), "Could not reorder.");
  };
  const addProducts = (items) => {
    setDialog(null);
    call(client.post(`/api/projects/${projectId}/items`, { items: items.map((item) => ({ file_id: item.file_id })) }), "Could not add those products.");
  };
  const link = (kind, id) => {
    setDialog(null);
    const url = kind === "board" ? "boards" : "visualisations";
    call(client.post(`/api/projects/${projectId}/${url}`, kind === "board" ? { board_id: id } : { visualisation_id: id }), "Could not add it.");
  };
  const unlink = (kind, id) =>
    call(client.delete(`/api/projects/${projectId}/${kind === "board" ? "boards" : "visualisations"}/${id}`), "Could not remove it.");

  const newBoard = async () => {
    try {
      const { data } = await client.post("/api/moodboards", {
        title: `${project.name} – concept`.slice(0, 120),
        aspect: "landscape",
        background: "#ffffff",
        items: buildTemplate("hero", "landscape"),
      });
      await client.post(`/api/projects/${projectId}/boards`, { board_id: data.id });
      navigate(`/moodboards/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not create the board.");
    }
  };
  const newWall = async () => {
    try {
      const { data } = await client.post("/api/visualisations", { title: `${project.name} – wall`.slice(0, 120), project_id: projectId });
      navigate(`/visualiser/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not create the wall.");
    }
  };
  const downloadAll = async () => {
    setDownloading(true);
    await downloadFile(`/api/projects/${projectId}/download`, {}, `${project.name} - Files.zip`);
    setDownloading(false);
  };

  if (error) {
    return (
      <main className="mb-page ar-page">
        <p className="mb-error">{error}</p>
        <Link to="/projects" className="mb-text-button">
          Back to projects
        </Link>
      </main>
    );
  }
  if (!project) {
    return (
      <main className="mb-page ar-page">
        <p className="mb-muted">Loading project...</p>
      </main>
    );
  }

  const { totals, units } = project;
  const stage = STATUSES.findIndex((entry) => entry.key === project.status);

  return (
    <main className="mb-page ar-page">
      <ToastContainer position="bottom-right" />
      <header className="ar-project-head">
        <Link to="/projects" className="mb-icon-button" aria-label="Back to projects" title="Back to projects">
          <ArrowLeft size={18} strokeWidth={1.8} />
        </Link>
        <BlurInput
          className="ar-title-input"
          type="text"
          maxLength={120}
          value={project.name}
          aria-label="Project name"
          required
          onCommit={(text) => update({ name: text })}
        />
        <div className="ar-head-actions">
          <button type="button" className="mb-tool" onClick={() => setDialog("firm")}>
            <Building2 size={16} strokeWidth={1.8} aria-hidden="true" />
            Firm details
          </button>
          <button type="button" className="mb-tool" onClick={downloadAll} disabled={downloading || !project.lines.length}>
            <Download size={16} strokeWidth={1.8} aria-hidden="true" />
            {downloading ? "Preparing..." : "All files"}
          </button>
          <button type="button" className="mb-primary" onClick={() => setDialog("document")} disabled={!project.lines.length}>
            <FileText size={16} strokeWidth={1.8} aria-hidden="true" />
            Quotation / spec
          </button>
        </div>
      </header>

      <ol className="ar-steps" aria-label="Project status">
        {STATUSES.map((entry, index) => (
          <li key={entry.key} className={index < stage ? "is-done" : index === stage ? "is-current" : ""}>
            <button type="button" onClick={() => update({ status: entry.key })} aria-current={index === stage ? "step" : undefined}>
              <span>{index + 1}</span>
              {entry.label}
            </button>
          </li>
        ))}
      </ol>

      <div className="ar-project-layout">
        <div className="ar-project-main">
          <section className="ar-card">
            <div className="ar-card-head">
              <h2>Products</h2>
              <button type="button" className="mb-primary" onClick={() => setDialog("products")}>
                <Plus size={15} strokeWidth={2} aria-hidden="true" />
                Add products
              </button>
            </div>
            {!project.lines.length ? (
              <p className="ar-muted">
                Add products from the catalogue or your favourites, then enter each wall's size to work out quantities.
              </p>
            ) : (
              <div className="ar-lines">
                {project.lines.map((line, index) => (
                  <ProjectLine
                    key={line.id}
                    line={line}
                    units={units}
                    index={index}
                    total={project.lines.length}
                    onSave={saveLine(line)}
                    onRemove={() => removeLine(line)}
                    onMove={(delta) => moveLine(index, delta)}
                  />
                ))}
              </div>
            )}
          </section>

          <section className="ar-card">
            <div className="ar-card-head">
              <h2>Mood boards</h2>
              <div className="ar-head-actions">
                <button type="button" className="mb-tool" onClick={() => setDialog("board")}>
                  <Link2 size={15} strokeWidth={1.8} aria-hidden="true" />
                  Add existing
                </button>
                <button type="button" className="mb-tool" onClick={newBoard}>
                  <LayoutTemplate size={15} strokeWidth={1.8} aria-hidden="true" />
                  New board
                </button>
              </div>
            </div>
            {!project.boards.length ? (
              <p className="ar-muted">No mood boards in this project yet.</p>
            ) : (
              <ul className="ar-linked">
                {project.boards.map((board) => (
                  <li key={board.id}>
                    <Link to={`/moodboards/${board.id}`}>{board.title}</Link>
                    <button type="button" className="mb-icon-button" onClick={() => unlink("board", board.id)} title="Take out of this project" aria-label={`Take ${board.title} out of this project`}>
                      <Unlink size={14} strokeWidth={1.8} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="ar-card">
            <div className="ar-card-head">
              <h2>Wall visualisations</h2>
              <div className="ar-head-actions">
                <button type="button" className="mb-tool" onClick={() => setDialog("wall")}>
                  <Link2 size={15} strokeWidth={1.8} aria-hidden="true" />
                  Add existing
                </button>
                <button type="button" className="mb-tool" onClick={newWall}>
                  <BrickWall size={15} strokeWidth={1.8} aria-hidden="true" />
                  New wall
                </button>
              </div>
            </div>
            {!project.visualisations.length ? (
              <p className="ar-muted">Show products on a photo of the client's wall, at their real size.</p>
            ) : (
              <div className="ar-wall-grid">
                {project.visualisations.map((wall) => (
                  <article key={wall.id} className="ar-wall-card">
                    <Link to={`/visualiser/${wall.id}`} className="ar-wall-photo">
                      {wall.photo_url ? <img src={absoluteUrl(wall.photo_url) || undefined} alt="" loading="lazy" /> : <BrickWall size={26} strokeWidth={1.4} />}
                    </Link>
                    <div>
                      <Link to={`/visualiser/${wall.id}`}>{wall.title}</Link>
                      <button type="button" className="mb-icon-button" onClick={() => unlink("wall", wall.id)} title="Take out of this project" aria-label={`Take ${wall.title} out of this project`}>
                        <Unlink size={14} strokeWidth={1.8} />
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>

        <aside className="ar-project-side">
          <section className="ar-card">
            <h2>Details</h2>
            <label className="ar-field">
              <span className="ar-field-label">Client</span>
              <BlurInput type="text" maxLength={120} value={project.client_name || ""} onCommit={(text) => update({ client_name: text })} />
            </label>
            <label className="ar-field">
              <span className="ar-field-label">Site</span>
              <BlurInput type="text" maxLength={200} value={project.site || ""} onCommit={(text) => update({ site: text })} />
            </label>
            <label className="ar-field">
              <span className="ar-field-label">Notes (only you see these)</span>
              <BlurInput multiline rows={3} maxLength={5000} value={project.notes || ""} onCommit={(text) => update({ notes: text })} />
            </label>
            <div className="ar-field">
              <span className="ar-field-label">Units</span>
              <UnitToggle units={units} onChange={(next) => update({ units: next })} />
            </div>
          </section>

          <section className="ar-card ar-totals">
            <h2>Quotation</h2>
            <div className="ar-row">
              <label className="ar-field">
                <span className="ar-field-label">Your markup (%)</span>
                <BlurInput type="number" min={0} max={500} step="0.5" value={String(project.markup_pct)} onCommit={(text) => update({ markup_pct: text || 0 })} />
              </label>
              <label className="ar-field">
                <span className="ar-field-label">GST (%)</span>
                <BlurInput type="number" min={0} max={100} step="0.5" value={String(project.gst_pct)} onCommit={(text) => update({ gst_pct: text || 0 })} />
              </label>
            </div>
            <dl>
              <div>
                <dt>Subtotal</dt>
                <dd>INR {formatMoney(totals.subtotal)}</dd>
              </div>
              <div>
                <dt>GST {totals.gst_pct}%</dt>
                <dd>INR {formatMoney(totals.gst)}</dd>
              </div>
              <div className="ar-total">
                <dt>Client total</dt>
                <dd>INR {formatMoney(totals.total)}</dd>
              </div>
            </dl>
            <p className="ar-muted ar-small">
              Your cost INR {formatMoney(totals.base_cost)} · your margin INR {formatMoney(totals.margin)} (not shown on the quotation)
            </p>
            {totals.incomplete > 0 && (
              <p className="ar-warning ar-small">
                {totals.incomplete} product{totals.incomplete === 1 ? " needs" : "s need"} a price or quantity.
              </p>
            )}
          </section>
        </aside>
      </div>

      {dialog === "products" && <ProductPicker title="Add products" multiple onClose={() => setDialog(null)} onPick={addProducts} />}
      {(dialog === "board" || dialog === "wall") && (
        <LinkPicker kind={dialog} project={project} onClose={() => setDialog(null)} onLink={(id) => link(dialog, id)} />
      )}
      {dialog === "document" && <DocumentDialog project={project} onClose={() => setDialog(null)} />}
      {dialog === "firm" && <FirmProfileDialog onClose={() => setDialog(null)} />}
    </main>
  );
}
